import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  useTheme,
  CircularProgress,
  Alert,
  Button,
  TextField,
  Chip,
  Tooltip,
  ButtonGroup,
  Stack,
  Divider,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  ShoppingCart as OrderIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
} from "@mui/icons-material";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import { useDispatch } from "react-redux";
import { getFlipkartFinanceDashboard } from "app/redux/slice/flipkartSlice";
import { useSnackbar } from "notistack";
import Loader from "@jumbo/components/Loader";
import { LocalizationProvider, DateRangePicker } from "@mui/lab";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import moment from "moment";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
} from "recharts";

const QUICK_RANGES = [
  {
    key: "today",
    label: "Today",
    getRange: () => [
      moment().startOf("day").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
  {
    key: "7d",
    label: "Last 7D",
    getRange: () => [
      moment().subtract(6, "days").startOf("day").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
  {
    key: "30d",
    label: "Last 30D",
    getRange: () => [
      moment().subtract(29, "days").startOf("day").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
  {
    key: "ytd",
    label: "YTD",
    getRange: () => [
      moment().startOf("year").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
];

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const FlipkartFinanceDashboard = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const [financeData, setFinanceData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState([null, null]);

  const handleApplyFilter = () => {
    if (dateRange[0] === null || dateRange[1] === null) {
      enqueueSnackbar("Please select both start and end date", {
        variant: "error",
      });
      return;
    }
    setLoading(true);
    const params = {
      fromDate: moment(dateRange[0]).format("YYYY-MM-DD"),
      toDate: moment(dateRange[1]).format("YYYY-MM-DD"),
    };
    dispatch(getFlipkartFinanceDashboard(params))
      .unwrap()
      .then((res) => {
        if (res?.status === 200) {
          setFinanceData(res?.data?.data);
        } else {
          enqueueSnackbar("Failed to fetch finance data", { variant: "error" });
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        enqueueSnackbar("Failed to fetch finance data", { variant: "error" });
      });
  };

  const fetchDashboard = useCallback(() => {
    setLoading(true);
    setDateRange([null, null]);
    dispatch(getFlipkartFinanceDashboard({}))
      .unwrap()
      .then((res) => {
        if (res?.status === 200) {
          setFinanceData(res?.data?.data);
        } else {
          enqueueSnackbar("Failed to fetch finance data", { variant: "error" });
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        enqueueSnackbar("Failed to fetch finance data", { variant: "error" });
      });
  }, [dispatch, enqueueSnackbar]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const metrics = React.useMemo(() => {
    if (!financeData) return [];
    const totalSale = financeData?.totalSale || {};
    const totalExpenses =
      financeData?.totalExpenses || financeData?.totalCost || {};
    const totalReward = financeData?.totalReward || {};
    const totalInput = financeData?.totalInput || {};
    const totalEarning =
      financeData?.totalEarning || financeData?.finalAmount || {};
    const returnAmount = financeData?.returnAmount || {};
    const saleAmount = financeData?.saleAmount || {};
    const settledAmount = financeData?.settledAmount || {};
    const pendingAmount = financeData?.pendingAmount || {};
    const bankSettlement = financeData?.bankSettlement || {};
    const totalCostPrice = financeData?.totalCostPrice || {};
    const settledCostPrice = financeData?.settledCostPrice || {};

    return [
      {
        key: "totalSale",
        title: "Accounted Net Sales",
        amount: totalSale?.amount || 0,
        subtitle: `${totalSale?.orderCount || 0} orders`,
        icon: <OrderIcon sx={{ color: theme.palette.primary.dark }} />,
        color: theme.palette.primary.main,
      },
      {
        key: "totalExpenses",
        title: "Total Expenses",
        amount: totalExpenses?.amount || 0,
        subtitle: `${totalExpenses?.orderCount || 0} orders`,
        icon: <MoneyOffIcon sx={{ color: theme.palette.error.dark }} />,
        color: theme.palette.error.main,
      },
      {
        key: "totalReward",
        title: "Total Rewards & Other Benefits",
        amount: totalReward?.amount || 0,
        subtitle: `${totalReward?.orderCount || 0} orders`,
        icon: <MonetizationOnIcon sx={{ color: theme.palette.success.dark }} />,
        color: theme.palette.success.main,
      },
      {
        key: "totalInput",
        title: "Total Input Tax Credits",
        amount: totalInput?.amount || 0,
        subtitle: `${totalInput?.orderCount || 0} orders`,
        icon: <MoneyOffIcon sx={{ color: theme.palette.info.dark }} />,
        color: theme.palette.info.main,
      },
      {
        key: "totalEarning",
        title: "Total Earning",
        amount: totalEarning?.amount || 0,
        subtitle: `${totalEarning?.orderCount || 0} orders`,
        icon: <MonetizationOnIcon sx={{ color: theme.palette.success.dark }} />,
        color: theme.palette.success.main,
      },
      {
        key: "settledAmount",
        title: "Settled Amount",
        amount: settledAmount?.amount || 0,
        subtitle: `${settledAmount?.orderCount || 0} payments`,
        icon: <AccountBalanceIcon sx={{ color: theme.palette.success.dark }} />,
        color: theme.palette.success.main,
      },
      {
        key: "returnAmount",
        title: "Return Amount",
        amount: returnAmount?.amount || 0,
        subtitle: `${returnAmount?.orderCount || 0} returns`,
        icon: (
          <AssignmentReturnIcon sx={{ color: theme.palette.warning.dark }} />
        ),
        color: theme.palette.warning.main,
      },
      {
        key: "saleAmount",
        title: "Sale Amount",
        amount: saleAmount?.amount || 0,
        subtitle: `${saleAmount?.orderCount || 0} orders`,
        icon: <MonetizationOnIcon sx={{ color: theme.palette.info.dark }} />,
        color: theme.palette.info.main,
      },
      {
        key: "pendingAmount",
        title: "Pending Amount",
        amount: pendingAmount?.amount || 0,
        subtitle: `${pendingAmount?.orderCount || 0} orders`,
        icon: <PendingActionsIcon sx={{ color: theme.palette.warning.dark }} />,
        color: theme.palette.warning.main,
      },
      {
        key: "bankSettlement",
        title: "Bank Settlement",
        amount: bankSettlement?.amount || 0,
        subtitle: `${bankSettlement?.orderCount || 0} payments`,
        icon: <AccountBalanceIcon sx={{ color: theme.palette.success.dark }} />,
        color: theme.palette.success.main,
      },
      {
        key: "totalCostPrice",
        title: "Total Cost",
        amount: totalCostPrice?.amount || 0,
        subtitle: `${totalCostPrice?.orderCount || 0} payments`,
        icon: <MoneyOffIcon sx={{ color: theme.palette.success.dark }} />,
        color: theme.palette.success.main,
      },
      {
        key: "settledCostPrice",
        title: "Settled Order Cost Price",
        amount: settledCostPrice?.amount || 0,
        subtitle: `${settledCostPrice?.orderCount || 0} payments`,
        icon: <MonetizationOnIcon sx={{ color: theme.palette.success.dark }} />,
        color: theme.palette.success.main,
      },
    ];
  }, [financeData, theme.palette]);

  const pl = financeData?.finalPL || {};
  const plStatus = String(pl?.status || "").toUpperCase();
  const isProfit = plStatus === "PROFIT";
  const isLoss = plStatus === "LOSS";
  const totalSaleAmount = Number(financeData?.totalSale?.amount || 0);
  const netMargin = Number(financeData?.netMargin || 0);

  const chartData = React.useMemo(() => {
    if (!financeData?.monthlyData) return [];
    return financeData.monthlyData.map((item) => ({
      month: item.month,
      Sale: Number(item?.totalSale || item?.saleAmount || 0),
      Expenses: Number(item?.totalExpenses ?? item?.totalCost ?? 0),
      Profit: Number(
        item?.profit?.amount ?? item?.totalProjected ?? item?.profit ?? 0,
      ),
    }));
  }, [financeData]);

  const MetricCard = ({ metric }) => (
    <Card
      elevation={1}
      sx={{
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        "&:hover": { transform: "translateY(-2px)", boxShadow: 4 },
      }}
    >
      <CardContent>
        <Box display="flex" alignItems="center" gap={2}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha(metric.color, 0.12),
            }}
          >
            {metric.icon}
          </Box>
          <Box>
            <Tooltip title={metric.title} placement="top-start">
              <Typography variant="subtitle2" color="text.secondary">
                {metric.title}
              </Typography>
            </Tooltip>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {formatCurrency(metric.amount)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {metric.subtitle}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <Typography variant="h4" component="h1" mb={2}>
        Flipkart Finance Dashboard
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid
            container
            spacing={2}
            alignItems="center"
          >
            <Grid item xs={12} sm={5}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateRangePicker
                  startText="From Date"
                  endText="To Date"
                  inputFormat="dd/MM/yyyy"
                  value={dateRange}
                  onChange={setDateRange}
                  renderInput={({ inputProps, ...startProps }, endProps) => {
                    const startValue = inputProps.value;
                    delete inputProps.value;
                    return (
                      <TextField
                        {...startProps}
                        inputProps={inputProps}
                        value={`${startValue || "DD/MM/YYYY"} - ${
                          endProps.inputProps.value || "DD/MM/YYYY"
                        }`}
                        fullWidth
                        size="small"
                      />
                    );
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Stack direction="row" spacing={1}>
                <ButtonGroup size="small" variant="outlined">
                  {QUICK_RANGES.map((r) => (
                    <Button
                      key={r.key}
                      onClick={() => setDateRange(r.getRange())}
                      disabled={loading}
                    >
                      {r.label}
                    </Button>
                  ))}
                </ButtonGroup>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                variant="contained"
                onClick={handleApplyFilter}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={16} /> : null}
                fullWidth
              >
                Apply
              </Button>
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                variant="outlined"
                onClick={fetchDashboard}
                disabled={loading}
                startIcon={<RefreshIcon />}
                fullWidth
              >
                Reset
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {loading ? (
        <Loader />
      ) : !financeData ? (
        <Alert severity="info">No data available.</Alert>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card
              sx={{
                background: isProfit
                  ? `linear-gradient(135deg, ${theme.palette.success.light}, ${theme.palette.success.main})`
                  : isLoss
                  ? `linear-gradient(135deg, ${alpha(
                      theme.palette.error.light,
                      0.6,
                    )}, ${alpha(theme.palette.error.main, 0.6)})`
                  : `linear-gradient(135deg, ${alpha(
                      theme.palette.warning.light,
                      0.6,
                    )}, ${alpha(theme.palette.warning.main, 0.6)})`,
                color: theme.palette.common.white,
              }}
            >
              <CardContent>
                <Grid container alignItems="center" spacing={2}>
                  <Grid item>
                    {isProfit ? (
                      <TrendingUpIcon fontSize="large" />
                    ) : (
                      <TrendingDownIcon fontSize="large" />
                    )}
                  </Grid>
                  <Grid item xs>
                    <Typography variant="h6">Total Profit / Loss</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {formatCurrency(pl?.amount || 0)}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.primary"
                      sx={{ mt: 1 }}
                    >
                      P/L = Accounted Net Sales - Total Expenses + Total Rewards & Other Benefits + Total Input Tax Credits - Settled Order Cost Price
                    </Typography>
                    <Typography variant="body2" color="text.primary">
                      = {formatCurrency(totalSaleAmount)} -{" "}
                      {formatCurrency(financeData?.totalExpenses?.amount || 0)} +{" "}
                      {formatCurrency(financeData?.totalReward?.amount || 0)} +{" "}
                      {formatCurrency(financeData?.totalInput?.amount || 0)} -{" "}
                      {formatCurrency(financeData?.settledCostPrice?.amount || 0)}
                    </Typography>
                    <Typography variant="body2" color="text.primary">
                      Net Margin: {netMargin.toFixed(2)}%
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Chip
                      label={pl?.status || "N/A"}
                      color={
                        isProfit ? "success" : isLoss ? "error" : "warning"
                      }
                      variant="filled"
                      sx={{ color: theme.palette.common.white, border: 1 }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{ mb: 1 }}
            >
              <Typography variant="h6">Overview</Typography>
              <Divider sx={{ flex: 1 }} />
            </Stack>
            <Grid container spacing={3}>
              {metrics.map((metric) => (
                <Grid key={metric.key} item xs={12} sm={6} md={4} lg={4}>
                  <MetricCard metric={metric} />
                </Grid>
              ))}
            </Grid>
          </Grid>

          {chartData.length > 0 && (
            <Grid item xs={12}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                sx={{ mb: 2 }}
              >
                <Typography variant="h6">Sales & Expenses Chart</Typography>
                <Divider sx={{ flex: 1 }} />
              </Stack>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>
                        Monthly Sales vs Expenses
                      </Typography>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" />
                          <YAxis />
                          <RechartsTooltip
                            formatter={(value) => formatCurrency(value)}
                          />
                          <Legend />
                          <Bar
                            dataKey="Sale"
                            fill={theme.palette.primary.main}
                          />
                          <Bar
                            dataKey="Expenses"
                            fill={theme.palette.error.main}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>
                        Monthly Profit / Loss Trend
                      </Typography>
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" />
                          <YAxis />
                          <RechartsTooltip
                            formatter={(value) => formatCurrency(value)}
                          />
                          <Legend />
                          <Line
                            type="monotone"
                            dataKey="Profit"
                            stroke={theme.palette.success.main}
                            strokeWidth={2}
                            dot={{ r: 4 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Grid>
          )}
        </Grid>
      )}
    </Box>
  );
};

export default FlipkartFinanceDashboard;
